import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import type { EstadoManejoVidro, IdVidro, ProtecoesManejo } from '../../dominio/didatico/manejoVidros'
import { executarManejoVidro } from '../../dominio/didatico/manejoVidros'
import { ehZonaTetoLateral, estadoInicialTetoLateral, executarTetoLateral } from '../../dominio/didatico/rebatimentoLateralTeto'
import { zonaTetoLateralPorId } from '../../visualizacao3d/oficina/zonasTetoLateral'
import { ALTURA_MAXIMA_ELEVACAO_PAINEL, ehZonaElevacao, estadoInicialElevacaoPainel, executarElevacaoPainel } from '../../dominio/didatico/elevacaoPainel'
import { zonaElevacaoPorId } from '../../visualizacao3d/oficina/zonasElevacaoPainel'
import { ehZonaRebatimento, estadoInicialRebatimentoPainel, executarRebatimentoPainel } from '../../dominio/didatico/rebatimentoPainel'
import { zonaRebatimentoPorId } from '../../visualizacao3d/oficina/zonasRebatimentoPainel'
import { DESLOCAMENTO_MAXIMO_PAINEL } from '../../dominio/didatico/oficinaExtensor'
import type { EtapaDemonstracaoCorte, TecnicaCorte } from '../../dominio/didatico/tecnicasCorteCatalogo'
import {
  estadoInicialPortaOficina,
  executarOperacaoPorta,
  portaProntaParaRemocao,
  type EstadoPortaOficina,
  type IdZonaOperacaoPorta,
  type OperacaoPorta,
  type ResultadoIntervencaoPorta,
  type ResultadoOperacaoPorta,
} from '../../dominio/didatico/oficinaCorte'
import { tocarSomCorteMetal, tocarSomEstilhacoVidro } from '../audio/somOperacional'
import {
  apoiosCompativeis,
  deslocarPainelComExtensor,
  estabilizacaoPreviaDaOficina,
  estadoInicialExtensorOficina,
  type EstadoExtensorOficina,
} from '../../dominio/didatico/oficinaExtensor'
import {
  estadoInicialTampaOficina,
  executarOperacaoTampa,
  tampaProntaParaRemocao,
  type EstadoTampaOficina,
  type IdZonaOperacaoTampa,
  type OperacaoTampa,
  type ResultadoOperacaoTampa,
} from '../../dominio/didatico/oficinaPortaMalas'
import {
  abaProntaParaRemocao,
  estadoInicialTerceiraPortaOficina,
  executarOperacaoTerceiraPorta,
  type EstadoTerceiraPortaOficina,
  type IdZonaOperacaoTerceiraPorta,
  type OperacaoTerceiraPorta,
  type ResultadoOperacaoTerceiraPorta,
} from '../../dominio/didatico/oficinaTerceiraPorta'
import {
  chaveProgressoIntervencao,
  resolverInteracaoPorta,
  resolverInteracaoTampa,
  resolverInteracaoTerceiraPorta,
  type ModoAlargador,
} from '../../dominio/didatico/interacaoOficina'
import {
  AJUSTE_FERRAMENTA_INICIAL,
  avaliarContatoFerramentaNaPorta,
  posicaoFerramentaNaPorta,
  zonaOperacaoPorId,
  type AjusteFerramentaPorta,
  type IdPortaOficina,
} from '../../visualizacao3d/oficina/zonasOperacaoPorta'
import {
  avaliarContatoFerramentaNaTampa,
  posicaoFerramentaNaTampa,
  zonaOperacaoTampaPorId,
} from '../../visualizacao3d/oficina/zonasOperacaoTampa'
import {
  avaliarContatoFerramentaNaTerceiraPorta,
  posicaoFerramentaNaTerceiraPorta,
  zonaOperacaoTerceiraPortaPorId,
  type IdLadoTerceiraPorta,
} from '../../visualizacao3d/oficina/zonasOperacaoTerceiraPorta'
import {
  progressoAcionamento,
  VELOCIDADE_ACIONAMENTO,
} from '../../visualizacao3d/oficina/rampaAcionamento'
import { DURACAO_SAIDA_PORTA_S } from '../../visualizacao3d/oficina/saidaDaPorta'
import {
  ATALHO_MODO_ALARGADOR,
  EQUIPAMENTOS_DE_APOIO,
  FERRAMENTAS_DE_INTERVENCAO,
  MODOS_DO_ALARGADOR,
  MOTIVO_EQUIPAMENTO_DE_APOIO,
  ferramentaDaCaixa,
  ferramentaPorAtalho,
} from './ferramentasOficina'
import {
  usePlayerOficinaCorte,
} from './usePlayerOficinaCorte'
import type {
  ConfiguracaoOficinaCorte,
  FerramentaOficinaCorte,
  ModoOficinaCorte,
  PecaOficinaCorte,
} from '../PainelOficinaCorte'
import type { Ferramenta as FerramentaDaTrilha } from '../workspace/TrilhaFerramentas'
import {
  IconeInteriorExtracao,
  IconeRetirarPeca,
  IconeSustentarPeca,
  IconeVidro,
} from '../icones'

export type { ModoOficinaCorte, PecaOficinaCorte }

export interface InstantaneoHistoricoOficina {
  portas: Record<IdPortaOficina, EstadoPortaOficina>
  progresso: Record<IdPortaOficina, Record<string, number>>
  tampa: EstadoTampaOficina
  progressoTampa: Record<string, number>
  terceirasPortas: Record<IdLadoTerceiraPorta, EstadoTerceiraPortaOficina>
  progressoTerceirasPortas: Record<IdLadoTerceiraPorta, Record<string, number>>
  extensor: EstadoExtensorOficina
  progressoExtensor: number
  vidros: Record<IdVidro, EstadoManejoVidro>
  detritosNaVitima: boolean
  detritosNasProtecoes: boolean
}

export interface OpcoesSessaoOficinaCorte {
  abaAtiva: boolean
  perfil: PerfilVeiculo
  configuracaoCorte: ConfiguracaoOficinaCorte
  setConfiguracaoCorte: (configuracao: ConfiguracaoOficinaCorte) => void
  estadosVidros: Record<IdVidro, EstadoManejoVidro>
  setEstadosVidros: React.Dispatch<React.SetStateAction<Record<IdVidro, EstadoManejoVidro>>>
  protecoesVidros: ProtecoesManejo
  detritosNaVitima: boolean
  detritosNasProtecoes: boolean
  setDetritosNaVitima: React.Dispatch<React.SetStateAction<boolean>>
  setDetritosNasProtecoes: React.Dispatch<React.SetStateAction<boolean>>
  aoRomperVidroComDetritos?: (origem: [number, number, number]) => void
  aoRegistrarHistorico?: () => void
  aoEnquadrarOficina?: () => void
}

export function useSessaoOficinaCorte({
  abaAtiva,
  perfil,
  configuracaoCorte,
  setConfiguracaoCorte,
  estadosVidros,
  setEstadosVidros,
  protecoesVidros,
  detritosNaVitima,
  detritosNasProtecoes,
  setDetritosNaVitima,
  setDetritosNasProtecoes,
  aoRomperVidroComDetritos,
  aoRegistrarHistorico,
  aoEnquadrarOficina,
}: OpcoesSessaoOficinaCorte) {
  const aoRomperVidroComDetritosRef = useRef(aoRomperVidroComDetritos)
  aoRomperVidroComDetritosRef.current = aoRomperVidroComDetritos
  const aoRegistrarHistoricoRef = useRef(aoRegistrarHistorico)
  aoRegistrarHistoricoRef.current = aoRegistrarHistorico
  const aoEnquadrarOficinaRef = useRef(aoEnquadrarOficina)
  aoEnquadrarOficinaRef.current = aoEnquadrarOficina

  const [modoOficinaCorte, setModoOficinaCorte] = useState<ModoOficinaCorte>('demonstracao')
  const [pecaOficinaCorte, setPecaOficinaCorte] = useState<PecaOficinaCorte>('porta')
  const [ferramentaCorte, setFerramentaCorte] = useState<FerramentaOficinaCorte>('cortadorHidraulico')
  const [modoAlargador, setModoAlargador] = useState<ModoAlargador>('abrir')
  const [mensagemInteracaoCorte, setMensagemInteracaoCorte] = useState(
    'Escolha uma ferramenta e pressione um ponto compatível no veículo.',
  )

  const [vidroCorte, setVidroCorte] = useState<IdVidro>('portaDianteiraEsquerda')
  const idPortaCorte = vidroCorte as IdPortaOficina
  const [resultadoIntervencaoCorte, setResultadoIntervencaoCorte] = useState<ResultadoIntervencaoPorta | null>(null)

  const [estadosPortasCorte, setEstadosPortasCorte] = useState<Record<IdPortaOficina, EstadoPortaOficina>>(() => ({
    portaDianteiraEsquerda: estadoInicialPortaOficina(),
    portaDianteiraDireita: estadoInicialPortaOficina(),
    portaTraseiraEsquerda: estadoInicialPortaOficina(),
    portaTraseiraDireita: estadoInicialPortaOficina(),
  }))
  const estadoPortaCorte = estadosPortasCorte[idPortaCorte]

  const [zonaOperacaoCorte, setZonaOperacaoCorte] = useState<IdZonaOperacaoPorta | null>(null)
  const [operacaoPortaCorte, setOperacaoPortaCorte] = useState<OperacaoPorta>('testarAberturaManual')
  const [resultadoOperacaoCorte, setResultadoOperacaoCorte] = useState<ResultadoOperacaoPorta | null>(null)
  const [progressoFerramentaCorte, setProgressoFerramentaCorte] = useState(0)

  const [progressoEstruturalPortas, setProgressoEstruturalPortas] = useState<
    Record<IdPortaOficina, Record<string, number>>
  >(() => ({
    portaDianteiraEsquerda: {},
    portaDianteiraDireita: {},
    portaTraseiraEsquerda: {},
    portaTraseiraDireita: {},
  }))

  const [estadoExtensorCorte, setEstadoExtensorCorte] = useState<EstadoExtensorOficina>(
    () => estadoInicialExtensorOficina(),
  )
  const [progressoEstruturalExtensor, setProgressoEstruturalExtensor] = useState(0)
  const [segurandoFerramentaCorte, setSegurandoFerramentaCorte] = useState(false)
  const [ajusteFerramentaCorte, setAjusteFerramentaCorte] = useState<AjusteFerramentaPorta>(
    () => ({ ...AJUSTE_FERRAMENTA_INICIAL }),
  )

  // Tampa traseira
  const [estadoTampaCorte, setEstadoTampaCorte] = useState<EstadoTampaOficina>(() => estadoInicialTampaOficina())
  const [progressoEstruturalTampa, setProgressoEstruturalTampa] = useState<Record<string, number>>({})
  const [operacaoTampaCorte, setOperacaoTampaCorte] = useState<OperacaoTampa>('testarAberturaManual')
  const [resultadoOperacaoTampaCorte, setResultadoOperacaoTampaCorte] = useState<ResultadoOperacaoTampa | null>(null)
  const [zonaOperacaoTampaCorte, setZonaOperacaoTampaCorte] = useState<IdZonaOperacaoTampa | null>(null)
  const [ajusteFerramentaTampaCorte, setAjusteFerramentaTampaCorte] = useState<AjusteFerramentaPorta>(
    () => ({ ...AJUSTE_FERRAMENTA_INICIAL }),
  )

  // Terceira porta
  const [ladoTerceiraPortaCorte, setLadoTerceiraPortaCorte] = useState<IdLadoTerceiraPorta>('esquerdo')
  const [estadosTerceirasPortasCorte, setEstadosTerceirasPortasCorte] = useState<
    Record<IdLadoTerceiraPorta, EstadoTerceiraPortaOficina>
  >(() => ({
    esquerdo: estadoInicialTerceiraPortaOficina(),
    direito: estadoInicialTerceiraPortaOficina(),
  }))
  const estadoTerceiraPortaCorte = estadosTerceirasPortasCorte[ladoTerceiraPortaCorte]
  const setEstadoTerceiraPortaCorte = useCallback((estado: EstadoTerceiraPortaOficina) => {
    setEstadosTerceirasPortasCorte((atuais) => ({ ...atuais, [ladoTerceiraPortaCorte]: estado }))
  }, [ladoTerceiraPortaCorte])

  const [progressoEstruturalTerceirasPortas, setProgressoEstruturalTerceirasPortas] = useState<
    Record<IdLadoTerceiraPorta, Record<string, number>>
  >({ esquerdo: {}, direito: {} })
  const [operacaoTerceiraPortaCorte, setOperacaoTerceiraPortaCorte] = useState<OperacaoTerceiraPorta>('cortarLadoSuperior')
  const [resultadoOperacaoTerceiraPortaCorte, setResultadoOperacaoTerceiraPortaCorte] = useState<ResultadoOperacaoTerceiraPorta | null>(null)
  const [zonaOperacaoTerceiraPortaCorte, setZonaOperacaoTerceiraPortaCorte] = useState<IdZonaOperacaoTerceiraPorta | null>(null)
  const [ajusteFerramentaTerceiraPortaCorte, setAjusteFerramentaTerceiraPortaCorte] = useState<AjusteFerramentaPorta>(
    () => ({ ...AJUSTE_FERRAMENTA_INICIAL }),
  )

  const chaveProgressoPortaAtual = chaveProgressoIntervencao(operacaoPortaCorte, zonaOperacaoCorte)
  const progressoEstruturalPortaAtual = progressoEstruturalPortas[idPortaCorte][chaveProgressoPortaAtual] ?? 0
  const chaveProgressoTampaAtual = chaveProgressoIntervencao(operacaoTampaCorte, zonaOperacaoTampaCorte)
  const chaveProgressoTerceiraPortaAtual = chaveProgressoIntervencao(operacaoTerceiraPortaCorte, zonaOperacaoTerceiraPortaCorte)

  const fluxoExtensorPronto = pecaOficinaCorte === 'porta'
    && ferramentaCorte === 'extensor'
    && apoiosCompativeis(estadoExtensorCorte.primeiroApoio, estadoExtensorCorte.segundoApoio)

  const progressoEstruturalAtual = fluxoExtensorPronto
    ? progressoEstruturalExtensor
    : pecaOficinaCorte === 'porta'
      ? progressoEstruturalPortaAtual
    : pecaOficinaCorte === 'tampaTraseira'
      ? progressoEstruturalTampa[chaveProgressoTampaAtual] ?? 0
      : progressoEstruturalTerceirasPortas[ladoTerceiraPortaCorte][chaveProgressoTerceiraPortaAtual] ?? 0

  const progressoEstruturalAtualRef = useRef(0)
  progressoEstruturalAtualRef.current = progressoEstruturalAtual

  const progressoFerramentaCorteRef = useRef(0)
  progressoFerramentaCorteRef.current = progressoFerramentaCorte

  const atualizarEstadoPortaCorte = useCallback((
    atualizacao: EstadoPortaOficina | ((atual: EstadoPortaOficina) => EstadoPortaOficina),
  ) => {
    setEstadosPortasCorte((atuais) => {
      const atual = atuais[idPortaCorte]
      const proximo = typeof atualizacao === 'function' ? atualizacao(atual) : atualizacao
      return { ...atuais, [idPortaCorte]: proximo }
    })
  }, [idPortaCorte])

  const atualizarProgressoEstruturalPorta = useCallback((
    operacao: OperacaoPorta,
    zona: IdZonaOperacaoPorta | null,
    progresso: number,
  ) => {
    setProgressoEstruturalPortas((atuais) => ({
      ...atuais,
      [idPortaCorte]: {
        ...atuais[idPortaCorte],
        [chaveProgressoIntervencao(operacao, zona)]: Math.max(0, Math.min(1, progresso)),
      },
    }))
  }, [idPortaCorte])

  const resetarEstadosOficinaCorte = useCallback(() => {
    setEstadosPortasCorte({
      portaDianteiraEsquerda: estadoInicialPortaOficina(),
      portaDianteiraDireita: estadoInicialPortaOficina(),
      portaTraseiraEsquerda: estadoInicialPortaOficina(),
      portaTraseiraDireita: estadoInicialPortaOficina(),
    })
    setEstadoTampaCorte(estadoInicialTampaOficina())
    setEstadosTerceirasPortasCorte({
      esquerdo: estadoInicialTerceiraPortaOficina(),
      direito: estadoInicialTerceiraPortaOficina(),
    })
    setEstadoExtensorCorte(estadoInicialExtensorOficina())
    setProgressoEstruturalPortas({
      portaDianteiraEsquerda: {},
      portaDianteiraDireita: {},
      portaTraseiraEsquerda: {},
      portaTraseiraDireita: {},
    })
    setProgressoEstruturalTampa({})
    setProgressoEstruturalTerceirasPortas({ esquerdo: {}, direito: {} })
    setProgressoEstruturalExtensor(0)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    setResultadoOperacaoCorte(null)
    setResultadoIntervencaoCorte(null)
  }, [])

  const aoConcluirEtapaPlayerCorte = useCallback(
    (etapa: EtapaDemonstracaoCorte) => {
      if (etapa.requerManejoVidro) {
        setEstadosVidros((atuais) => ({
          ...atuais,
          [vidroCorte]: 'removido',
        }))
        setEstadosPortasCorte((atuais) => ({
          ...atuais,
          [idPortaCorte]: {
            ...atuais[idPortaCorte],
            vidro: 'removido',
          },
        }))
      }
      if (etapa.operacaoTetoLateral) {
        const resultado = executarTetoLateral(estadoExtensorCorte.tetoLateral ?? estadoInicialTetoLateral(), etapa.operacaoTetoLateral, {
          configuracao: configuracaoCorte, estabilizado: estabilizacaoPreviaDaOficina(configuracaoCorte, perfil).confirmada,
          ferramenta: etapa.ferramenta === 'nenhuma' ? undefined : etapa.ferramenta, zona: etapa.zonaAlvo,
        })
        if (resultado.concluiu) {
          setEstadoExtensorCorte((atual) => ({ ...atual, tetoLateral: resultado.estado, deslocamentoPainel: 0, concluida: resultado.estado.rebatido }))
        }
        setMensagemInteracaoCorte(resultado.mensagem)
        return resultado.concluiu
      }
      if (etapa.operacaoElevacao) {
        const resultado = executarElevacaoPainel(estadoExtensorCorte.elevacao ?? estadoInicialElevacaoPainel(), etapa.operacaoElevacao, {
          portaRemovida: estadosPortasCorte[idPortaCorte].removida,
          portaDianteira: idPortaCorte.startsWith('portaDianteira'),
          estabilizado: estabilizacaoPreviaDaOficina(configuracaoCorte, perfil).confirmada,
          ferramenta: etapa.ferramenta === 'nenhuma' ? undefined : etapa.ferramenta,
          modoAlargador: etapa.modoAlargador, zona: etapa.zonaAlvo,
        })
        if (resultado.concluiu) setEstadoExtensorCorte((atual) => ({
          ...atual, elevacao: resultado.estado, deslocamentoPainel: 0, concluida: resultado.estado.acionado,
        }))
        setMensagemInteracaoCorte(resultado.mensagem)
        return resultado.concluiu
      }
      if (etapa.operacaoPainel) {
        const resultado = executarRebatimentoPainel(estadoExtensorCorte.rebatimento ?? estadoInicialRebatimentoPainel(), etapa.operacaoPainel, {
          portaRemovida: estadosPortasCorte[idPortaCorte].removida,
          portaDianteira: idPortaCorte.startsWith('portaDianteira'),
          estabilizado: estabilizacaoPreviaDaOficina(configuracaoCorte, perfil).confirmada,
          ferramenta: etapa.ferramenta === 'nenhuma' ? undefined : etapa.ferramenta, zona: etapa.zonaAlvo,
        })
        if (resultado.concluiu) setEstadoExtensorCorte((atual) => ({
          ...atual, rebatimento: resultado.estado,
          primeiroApoio: resultado.estado.extensorPosicionado ? 'baseSoleira' : null,
          segundoApoio: resultado.estado.extensorPosicionado ? 'painel' : null,
          deslocamentoPainel: resultado.estado.acionado ? DESLOCAMENTO_MAXIMO_PAINEL : 0,
          concluida: resultado.estado.acionado,
        }))
        setMensagemInteracaoCorte(resultado.mensagem)
        return resultado.concluiu
      }
      if (etapa.operacaoPorta) {
        const resultado = executarOperacaoPorta(estadosPortasCorte[idPortaCorte], {
          operacao: etapa.operacaoPorta,
          ferramenta: etapa.ferramenta === 'nenhuma' ? undefined : etapa.ferramenta,
          zona: etapa.zonaAlvo as any,
          orientacaoValida: true,
          portaDianteira: idPortaCorte.startsWith('portaDianteira'),
        })
        setEstadosPortasCorte((atuais) => ({ ...atuais, [idPortaCorte]: resultado.estado }))
        return resultado.concluiu
      }
      if (etapa.operacaoTampa) {
        const resultado = executarOperacaoTampa(estadoTampaCorte, {
          operacao: etapa.operacaoTampa,
          ferramenta: etapa.ferramenta === 'nenhuma' ? undefined : etapa.ferramenta,
          zona: etapa.zonaAlvo as any,
          orientacaoValida: true,
        })
        setEstadoTampaCorte(resultado.estado)
        return resultado.concluiu
      }
      if (etapa.operacaoTerceiraPorta) {
        const resultado = executarOperacaoTerceiraPorta(estadosTerceirasPortasCorte[ladoTerceiraPortaCorte], {
          operacao: etapa.operacaoTerceiraPorta,
          ferramenta: etapa.ferramenta === 'nenhuma' ? undefined : etapa.ferramenta,
          zona: etapa.zonaAlvo as any,
          orientacaoValida: true,
        })
        setEstadosTerceirasPortasCorte((atuais) => ({ ...atuais, [ladoTerceiraPortaCorte]: resultado.estado }))
        return resultado.concluiu
      }
      return true
    },
    [setEstadosVidros, vidroCorte, idPortaCorte, estadosPortasCorte, estadoTampaCorte, estadosTerceirasPortasCorte, ladoTerceiraPortaCorte, estadoExtensorCorte, configuracaoCorte, perfil],
  )

  const aoMudarTecnicaPlayerCorte = useCallback(
    (novaTecnica: TecnicaCorte) => {
      if (novaTecnica.id === 'rebatimentoPainel' || novaTecnica.id === 'elevacaoPainel') setVidroCorte(idPortaCorte.endsWith('Direita') ? 'portaDianteiraDireita' : 'portaDianteiraEsquerda')
      if (novaTecnica.id === 'rebatimentoLateralTeto') setVidroCorte('portaDianteiraDireita')
      setPecaOficinaCorte(novaTecnica.peca)
      setConfiguracaoCorte(novaTecnica.configuracao)
      resetarEstadosOficinaCorte()
    },
    [resetarEstadosOficinaCorte, setConfiguracaoCorte, idPortaCorte],
  )

  const playerCorte = usePlayerOficinaCorte({
    aoConcluirEtapa: aoConcluirEtapaPlayerCorte,
    aoMudarTecnica: aoMudarTecnicaPlayerCorte,
    aoReiniciar: resetarEstadosOficinaCorte,
  })

  const estadoExtensorVisual: EstadoExtensorOficina = playerCorte.tecnica.id === 'rebatimentoPainel' && modoOficinaCorte === 'demonstracao'
    ? {
        ...estadoExtensorCorte,
        rebatimento: estadoExtensorCorte.rebatimento ?? estadoInicialRebatimentoPainel(),
        deslocamentoPainel: estadoExtensorCorte.rebatimento?.acionado ? DESLOCAMENTO_MAXIMO_PAINEL
          : playerCorte.etapaAtual.operacaoPainel === 'acionarExtensor' && playerCorte.faseVisual === 'executando'
            ? DESLOCAMENTO_MAXIMO_PAINEL * playerCorte.progressoFerramenta : 0,
      }
    : playerCorte.tecnica.id === 'elevacaoPainel' && modoOficinaCorte === 'demonstracao'
      ? {
          ...estadoExtensorCorte,
          deslocamentoPainel: 0,
          elevacao: {
            ...(estadoExtensorCorte.elevacao ?? estadoInicialElevacaoPainel()),
            progressoDobra: estadoExtensorCorte.elevacao?.abaDobrada ? 1
              : playerCorte.etapaAtual.operacaoElevacao === 'dobrarAba' && playerCorte.faseVisual === 'executando'
                ? playerCorte.progressoFerramenta : 0,
            alturaPainel: estadoExtensorCorte.elevacao?.acionado ? ALTURA_MAXIMA_ELEVACAO_PAINEL
              : playerCorte.etapaAtual.operacaoElevacao === 'elevarPainel' && playerCorte.faseVisual === 'executando'
                ? ALTURA_MAXIMA_ELEVACAO_PAINEL * playerCorte.progressoFerramenta : 0,
          },
        }
      : playerCorte.tecnica.id === 'rebatimentoLateralTeto' && modoOficinaCorte === 'demonstracao'
        ? {
            ...estadoExtensorCorte,
            tetoLateral: {
              ...(estadoExtensorCorte.tetoLateral ?? estadoInicialTetoLateral()),
              progressoRebatimento: estadoExtensorCorte.tetoLateral?.rebatido ? 1
                : playerCorte.etapaAtual.operacaoTetoLateral === 'rebaterTeto' && playerCorte.faseVisual === 'executando'
                  ? playerCorte.progressoFerramenta : 0,
            },
          }
        : estadoExtensorCorte

  const ajusteFerramentaPlayerCorte = useMemo<AjusteFerramentaPorta>(() => {
    const zonaId = playerCorte.etapaAtual.zonaAlvo
    if (!zonaId) return AJUSTE_FERRAMENTA_INICIAL
    if (ehZonaTetoLateral(zonaId)) return { ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zonaTetoLateralPorId(perfil, zonaId).anguloGraus }
    if (ehZonaElevacao(zonaId)) return { ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zonaElevacaoPorId(perfil, idPortaCorte, zonaId).anguloGraus }
    if (ehZonaRebatimento(zonaId)) return { ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zonaRebatimentoPorId(perfil, idPortaCorte, zonaId).anguloGraus }
    if (pecaOficinaCorte === 'porta') {
      const zona = zonaOperacaoPorId(perfil, idPortaCorte, zonaId as IdZonaOperacaoPorta)
      return { ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zona?.anguloGraus ?? 0 }
    }
    if (pecaOficinaCorte === 'tampaTraseira') {
      const zona = zonaOperacaoTampaPorId(perfil, zonaId as IdZonaOperacaoTampa)
      return { ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zona?.anguloGraus ?? 0 }
    }
    if (pecaOficinaCorte === 'terceiraPorta') {
      const zona = zonaOperacaoTerceiraPortaPorId(perfil, ladoTerceiraPortaCorte, zonaId as IdZonaOperacaoTerceiraPorta)
      return { ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zona?.anguloGraus ?? 0 }
    }
    return AJUSTE_FERRAMENTA_INICIAL
  }, [playerCorte.etapaAtual.zonaAlvo, pecaOficinaCorte, perfil, idPortaCorte, ladoTerceiraPortaCorte])

  const condicaoEstabilizacaoExtensor = useMemo(
    () => estabilizacaoPreviaDaOficina(configuracaoCorte, perfil),
    [configuracaoCorte, perfil],
  )

  const posicaoFerramentaCorte = useMemo(
    () => posicaoFerramentaNaPorta(perfil, idPortaCorte, zonaOperacaoCorte, ajusteFerramentaCorte),
    [perfil, idPortaCorte, zonaOperacaoCorte, ajusteFerramentaCorte],
  )
  const contatoFerramentaCorte = useMemo(
    () => avaliarContatoFerramentaNaPorta(
      perfil,
      idPortaCorte,
      posicaoFerramentaCorte,
      ajusteFerramentaCorte.anguloGraus,
    ),
    [perfil, idPortaCorte, posicaoFerramentaCorte, ajusteFerramentaCorte.anguloGraus],
  )

  const posicaoFerramentaTampaCorte = useMemo(
    () => posicaoFerramentaNaTampa(perfil, zonaOperacaoTampaCorte, ajusteFerramentaTampaCorte),
    [perfil, zonaOperacaoTampaCorte, ajusteFerramentaTampaCorte],
  )
  const contatoFerramentaTampaCorte = useMemo(
    () => avaliarContatoFerramentaNaTampa(perfil, posicaoFerramentaTampaCorte, ajusteFerramentaTampaCorte.anguloGraus),
    [perfil, posicaoFerramentaTampaCorte, ajusteFerramentaTampaCorte.anguloGraus],
  )

  const idTerceiraPortaCorte = ladoTerceiraPortaCorte === 'esquerdo' ? 'terceiraPortaEsquerda' : 'terceiraPortaDireita'
  const posicaoFerramentaTerceiraPortaCorte = useMemo(
    () => posicaoFerramentaNaTerceiraPorta(perfil, ladoTerceiraPortaCorte, zonaOperacaoTerceiraPortaCorte, ajusteFerramentaTerceiraPortaCorte),
    [perfil, ladoTerceiraPortaCorte, zonaOperacaoTerceiraPortaCorte, ajusteFerramentaTerceiraPortaCorte],
  )
  const contatoFerramentaTerceiraPortaCorte = useMemo(
    () => avaliarContatoFerramentaNaTerceiraPorta(
      perfil,
      ladoTerceiraPortaCorte,
      posicaoFerramentaTerceiraPortaCorte,
      ajusteFerramentaTerceiraPortaCorte.anguloGraus,
    ),
    [perfil, ladoTerceiraPortaCorte, posicaoFerramentaTerceiraPortaCorte, ajusteFerramentaTerceiraPortaCorte.anguloGraus],
  )

  // Câmera da oficina
  useEffect(() => {
    if (!abaAtiva) return
    if (playerCorte.cameraGuiada && aoEnquadrarOficinaRef.current) {
      aoEnquadrarOficinaRef.current()
    }
  }, [
    abaAtiva,
    pecaOficinaCorte,
    idPortaCorte,
    idTerceiraPortaCorte,
    ladoTerceiraPortaCorte,
    configuracaoCorte,
    playerCorte.cameraGuiada,
    playerCorte.versaoFocoCamera,
  ])

  // Ações de execução
  const executarAcaoPorta = useCallback((operacao: OperacaoPorta = operacaoPortaCorte) => {
    const resultado = executarOperacaoPorta(
      { ...estadosPortasCorte[idPortaCorte], vidro: estadosVidros[vidroCorte] },
      {
        operacao,
        ferramenta: ferramentaCorte,
        zona: contatoFerramentaCorte.contatoValido ? contatoFerramentaCorte.zona : (zonaOperacaoCorte ?? null),
        orientacaoValida: contatoFerramentaCorte.contatoValido ? contatoFerramentaCorte.orientacaoValida : true,
        portaDianteira: idPortaCorte.startsWith('portaDianteira'),
      },
    )
    atualizarEstadoPortaCorte(resultado.estado)
    setResultadoOperacaoCorte(resultado)
    setMensagemInteracaoCorte(resultado.mensagem)
    if (resultado.concluiu) {
      tocarSomCorteMetal()
    }
    if (resultado.estado.vidro !== estadosVidros[vidroCorte]) {
      setEstadosVidros((atuais) => ({ ...atuais, [vidroCorte]: resultado.estado.vidro }))
      if (resultado.codigo === 'vidroRompeu') {
        tocarSomEstilhacoVidro()
        setSegurandoFerramentaCorte(false)
        setDetritosNaVitima(true)
        aoRomperVidroComDetritosRef.current?.([-perfil.largura / 2, (perfil.cinturaY + perfil.tetoY) / 2, perfil.colunaB.z])
      }
    }
    return resultado
  }, [
    operacaoPortaCorte,
    estadosPortasCorte,
    idPortaCorte,
    estadosVidros,
    vidroCorte,
    ferramentaCorte,
    contatoFerramentaCorte,
    zonaOperacaoCorte,
    atualizarEstadoPortaCorte,
    setEstadosVidros,
    setDetritosNaVitima,
    perfil,
  ])

  const executarAcaoManualPorta = useCallback((operacao: 'testarAberturaManual' | 'sustentarPorta' | 'removerPorta') => {
    aoRegistrarHistoricoRef.current?.()
    setOperacaoPortaCorte(operacao)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    setZonaOperacaoCorte(null)
    const resultado = executarAcaoPorta(operacao)
    setMensagemInteracaoCorte(resultado.mensagem)
  }, [executarAcaoPorta])

  const gerenciarVidroCorte = useCallback(() => {
    aoRegistrarHistoricoRef.current?.()
    const resultado = executarManejoVidro(vidroCorte, 'puncaoAutomatica', protecoesVidros)
    if (resultado.sucesso) {
      tocarSomEstilhacoVidro()
    }
    setEstadosVidros((atuais) => ({ ...atuais, [vidroCorte]: resultado.novoEstado }))
    atualizarEstadoPortaCorte((atual) => ({ ...atual, vidro: resultado.novoEstado }))
    if (resultado.detritosNaVitima) setDetritosNaVitima(true)
    if (resultado.detritosNasProtecoes) setDetritosNasProtecoes(true)
    const mensagem = resultado.sucesso
      ? 'Vidro gerenciado antes da intervenção. A porta está pronta para a técnica selecionada.'
      : resultado.mensagemDidatica
    setResultadoIntervencaoCorte({
      permitido: resultado.sucesso,
      rompeuVidro: false,
      novoEstadoVidro: resultado.novoEstado,
      mensagem,
    })
    setMensagemInteracaoCorte(mensagem)
  }, [
    vidroCorte,
    protecoesVidros,
    setEstadosVidros,
    atualizarEstadoPortaCorte,
    setDetritosNaVitima,
    setDetritosNasProtecoes,
  ])

  const executarAcaoTampa = useCallback(() => {
    const resultado = executarOperacaoTampa(estadoTampaCorte, {
      operacao: operacaoTampaCorte,
      ferramenta: ferramentaCorte,
      zona: contatoFerramentaTampaCorte.contatoValido ? contatoFerramentaTampaCorte.zona : null,
      orientacaoValida: contatoFerramentaTampaCorte.orientacaoValida,
    })
    setEstadoTampaCorte(resultado.estado)
    setResultadoOperacaoTampaCorte(resultado)
    setMensagemInteracaoCorte(resultado.mensagem)
    return resultado
  }, [estadoTampaCorte, operacaoTampaCorte, ferramentaCorte, contatoFerramentaTampaCorte])

  const executarAcaoManualTampa = useCallback((operacao: 'testarAberturaManual' | 'sustentarTampa' | 'removerTampa') => {
    aoRegistrarHistoricoRef.current?.()
    setOperacaoTampaCorte(operacao)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    setZonaOperacaoTampaCorte(null)
    const resultado = executarOperacaoTampa(estadoTampaCorte, { operacao })
    setEstadoTampaCorte(resultado.estado)
    setResultadoOperacaoTampaCorte(resultado)
    setMensagemInteracaoCorte(resultado.mensagem)
  }, [estadoTampaCorte])

  const executarAcaoTerceiraPorta = useCallback(() => {
    const resultado = executarOperacaoTerceiraPorta(estadoTerceiraPortaCorte, {
      operacao: operacaoTerceiraPortaCorte,
      ferramenta: ferramentaCorte,
      zona: contatoFerramentaTerceiraPortaCorte.contatoValido ? contatoFerramentaTerceiraPortaCorte.zona : null,
      orientacaoValida: contatoFerramentaTerceiraPortaCorte.orientacaoValida,
    })
    setEstadoTerceiraPortaCorte(resultado.estado)
    setResultadoOperacaoTerceiraPortaCorte(resultado)
    setMensagemInteracaoCorte(resultado.mensagem)
    return resultado
  }, [estadoTerceiraPortaCorte, operacaoTerceiraPortaCorte, ferramentaCorte, contatoFerramentaTerceiraPortaCorte, setEstadoTerceiraPortaCorte])

  const executarAcaoManualTerceiraPorta = useCallback((operacao: 'sustentarAba' | 'removerAba') => {
    aoRegistrarHistoricoRef.current?.()
    setOperacaoTerceiraPortaCorte(operacao)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    setZonaOperacaoTerceiraPortaCorte(null)
    const resultado = executarOperacaoTerceiraPorta(estadoTerceiraPortaCorte, { operacao })
    setEstadoTerceiraPortaCorte(resultado.estado)
    setResultadoOperacaoTerceiraPortaCorte(resultado)
    setMensagemInteracaoCorte(resultado.mensagem)
  }, [estadoTerceiraPortaCorte, setEstadoTerceiraPortaCorte])

  const pausarAcionamentoFerramenta = useCallback(() => {
    setSegurandoFerramentaCorte(false)
    const parcial = progressoEstruturalAtualRef.current
    if (parcial > 0 && parcial < 1) {
      setMensagemInteracaoCorte(
        `Intervenção pausada em ${Math.round(parcial * 100)}%. Pressione o mesmo alvo para retomar.`,
      )
    }
  }, [])

  // Rampa de acionamento visual da ferramenta por relógio de parede
  useEffect(() => {
    if (!abaAtiva) return
    const alvo = segurandoFerramentaCorte ? 1 : 0
    const inicial = progressoFerramentaCorteRef.current
    if (!segurandoFerramentaCorte && inicial === 0) return
    const inicio = performance.now()
    let quadro: number
    const passo = (agora: number) => {
      const p = progressoAcionamento(inicial, alvo, (agora - inicio) / 1000)
      setProgressoFerramentaCorte(p)
      if ((alvo === 1 && p < 1) || (alvo === 0 && p > 0)) {
        quadro = requestAnimationFrame(passo)
      }
    }
    quadro = requestAnimationFrame(passo)
    return () => cancelAnimationFrame(quadro)
  }, [
    abaAtiva,
    segurandoFerramentaCorte,
    zonaOperacaoCorte,
    zonaOperacaoTampaCorte,
    zonaOperacaoTerceiraPortaCorte,
  ])

  // Rampa estrutural
  useEffect(() => {
    if (!abaAtiva || !segurandoFerramentaCorte) return

    const operacao = pecaOficinaCorte === 'porta'
      ? operacaoPortaCorte
      : pecaOficinaCorte === 'tampaTraseira' ? operacaoTampaCorte : operacaoTerceiraPortaCorte
    const zona = pecaOficinaCorte === 'porta'
      ? zonaOperacaoCorte
      : pecaOficinaCorte === 'tampaTraseira' ? zonaOperacaoTampaCorte : zonaOperacaoTerceiraPortaCorte
    const chave = chaveProgressoIntervencao(operacao, zona)
    const inicial = progressoEstruturalAtualRef.current
    const inicio = Date.now()
    let quadro: number
    const passo = () => {
      const proximo = Math.min(1, inicial + ((Date.now() - inicio) / 1000) * VELOCIDADE_ACIONAMENTO)
      if (fluxoExtensorPronto) {
        setProgressoEstruturalExtensor(proximo)
        setEstadoExtensorCorte((atual) => deslocarPainelComExtensor(atual, proximo))
      } else if (pecaOficinaCorte === 'porta') {
        setProgressoEstruturalPortas((atuais) => ({
          ...atuais,
          [idPortaCorte]: { ...atuais[idPortaCorte], [chave]: proximo },
        }))
      } else if (pecaOficinaCorte === 'tampaTraseira') {
        setProgressoEstruturalTampa((atual) => ({ ...atual, [chave]: proximo }))
      } else {
        setProgressoEstruturalTerceirasPortas((atuais) => ({
          ...atuais,
          [ladoTerceiraPortaCorte]: { ...atuais[ladoTerceiraPortaCorte], [chave]: proximo },
        }))
      }
      if (proximo < 1) quadro = requestAnimationFrame(passo)
    }
    quadro = requestAnimationFrame(passo)
    return () => cancelAnimationFrame(quadro)
  }, [
    abaAtiva,
    pecaOficinaCorte,
    segurandoFerramentaCorte,
    idPortaCorte,
    operacaoPortaCorte,
    zonaOperacaoCorte,
    operacaoTampaCorte,
    zonaOperacaoTampaCorte,
    operacaoTerceiraPortaCorte,
    zonaOperacaoTerceiraPortaCorte,
    ladoTerceiraPortaCorte,
    fluxoExtensorPronto,
  ])

  // Soltar ao sair do foco da janela
  useEffect(() => {
    if (!segurandoFerramentaCorte) return
    const soltar = () => pausarAcionamentoFerramenta()
    window.addEventListener('pointerup', soltar)
    window.addEventListener('pointercancel', soltar)
    window.addEventListener('blur', soltar)
    return () => {
      window.removeEventListener('pointerup', soltar)
      window.removeEventListener('pointercancel', soltar)
      window.removeEventListener('blur', soltar)
    }
  }, [segurandoFerramentaCorte, pausarAcionamentoFerramenta])

  // Cancelar com Escape
  useEffect(() => {
    if (!abaAtiva) return
    const cancelarComEscape = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape') return
      pausarAcionamentoFerramenta()
      setProgressoFerramentaCorte(0)
      const parcial = progressoEstruturalAtualRef.current
      setMensagemInteracaoCorte(
        parcial > 0 && parcial < 1
          ? `Intervenção cancelada em ${Math.round(parcial * 100)}%. O progresso foi preservado.`
          : 'Intervenção cancelada. A ferramenta continua equipada.',
      )
    }
    window.addEventListener('keydown', cancelarComEscape)
    return () => window.removeEventListener('keydown', cancelarComEscape)
  }, [abaAtiva, pausarAcionamentoFerramenta])

  // Saída da porta retirada
  const [progressoSaidaPortaCorte, setProgressoSaidaPortaCorte] = useState(0)
  useEffect(() => {
    if (!estadoPortaCorte.removida) {
      setProgressoSaidaPortaCorte(0)
      return
    }
    const inicio = performance.now()
    let quadro: number
    const passo = (agora: number) => {
      const decorrido = (agora - inicio) / 1000 / DURACAO_SAIDA_PORTA_S
      setProgressoSaidaPortaCorte(Math.min(1, decorrido))
      if (decorrido < 1) quadro = requestAnimationFrame(passo)
    }
    quadro = requestAnimationFrame(passo)
    return () => cancelAnimationFrame(quadro)
  }, [estadoPortaCorte.removida])

  // Auto-aplicar quando progresso atinge 1
  const jaAplicouNesteApertoRef = useRef(false)
  const executarAcaoDaPecaAtualRef = useRef(() => {})
  executarAcaoDaPecaAtualRef.current = pecaOficinaCorte === 'porta'
    ? () => { executarAcaoPorta() }
    : pecaOficinaCorte === 'tampaTraseira'
      ? () => { executarAcaoTampa() }
      : () => { executarAcaoTerceiraPorta() }

  useEffect(() => {
    if (!segurandoFerramentaCorte) {
      jaAplicouNesteApertoRef.current = false
      return
    }
    if (progressoEstruturalAtual < 1 || jaAplicouNesteApertoRef.current) return
    jaAplicouNesteApertoRef.current = true
    if (fluxoExtensorPronto) {
      setEstadoExtensorCorte((atual) => deslocarPainelComExtensor(atual, 1))
      setSegurandoFerramentaCorte(false)
      setMensagemInteracaoCorte('Painel rebatido até o limite didático; o espaço interno foi ampliado.')
      return
    }
    executarAcaoDaPecaAtualRef.current()
    if (pecaOficinaCorte === 'porta') {
      atualizarProgressoEstruturalPorta(operacaoPortaCorte, zonaOperacaoCorte, 0)
    } else if (pecaOficinaCorte === 'tampaTraseira') {
      setProgressoEstruturalTampa((atual) => ({ ...atual, [chaveProgressoTampaAtual]: 0 }))
    } else {
      setProgressoEstruturalTerceirasPortas((atuais) => ({
        ...atuais,
        [ladoTerceiraPortaCorte]: {
          ...atuais[ladoTerceiraPortaCorte],
          [chaveProgressoTerceiraPortaAtual]: 0,
        },
      }))
    }
  }, [
    progressoEstruturalAtual,
    segurandoFerramentaCorte,
    pecaOficinaCorte,
    operacaoPortaCorte,
    zonaOperacaoCorte,
    chaveProgressoTampaAtual,
    chaveProgressoTerceiraPortaAtual,
    ladoTerceiraPortaCorte,
    fluxoExtensorPronto,
    atualizarProgressoEstruturalPorta,
  ])

  const escolherFerramentaCorte = useCallback((ferramenta: FerramentaOficinaCorte) => {
    setFerramentaCorte(ferramenta)
    setResultadoIntervencaoCorte(null)
    setResultadoOperacaoCorte(null)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    const descritor = ferramentaDaCaixa(ferramenta)
    setMensagemInteracaoCorte(
      ferramenta === 'extensor'
        ? 'Extensor equipado. Selecione dois apoios na cena: a soleira estrutural e o painel móvel.'
        : `${descritor?.nome ?? 'Ferramenta'} equipada. ${descritor?.dica ?? ''} Segure o ponto de aplicação para acionar.`.replace(/\s+/g, ' '),
    )
  }, [])

  const escolherModoAlargador = useCallback((modo: ModoAlargador) => {
    setModoAlargador(modo)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    setMensagemInteracaoCorte(
      modo === 'abrir'
        ? 'Alargador em modo abrir: aponte para um vínculo ou para uma abertura existente.'
        : 'Alargador em modo fechar: aponte para a chapa ou para o para-lama dianteiro.',
    )
  }, [])

  const motivoExtensorIndisponivel = pecaOficinaCorte === 'porta'
    ? undefined
    : 'Os dois apoios estão modelados na porta: escolha a porta como peça alvo'

  const motivoDaFerramentaDeCorte = useCallback((ferramenta: FerramentaOficinaCorte) =>
    ferramenta === 'machadinha' || ferramenta === 'serraSabre'
      ? 'Ferramenta de vidro usada na demonstração de rebatimento lateral do teto.'
      : ferramenta === 'extensor' ? motivoExtensorIndisponivel : undefined,
  [motivoExtensorIndisponivel])

  const equiparFerramentaPorAtalho = useCallback((ferramenta: FerramentaOficinaCorte, comShift: boolean) => {
    if (ferramenta === 'alargador' && comShift && ferramentaCorte === 'alargador') {
      escolherModoAlargador(modoAlargador === 'abrir' ? 'fechar' : 'abrir')
      return
    }
    const motivo = motivoDaFerramentaDeCorte(ferramenta)
    if (motivo) {
      setMensagemInteracaoCorte(`${ferramentaDaCaixa(ferramenta)?.nome}: ${motivo}.`)
      return
    }
    escolherFerramentaCorte(ferramenta)
  }, [ferramentaCorte, modoAlargador, escolherModoAlargador, motivoDaFerramentaDeCorte, escolherFerramentaCorte])

  // Atalhos de teclado quando na oficina
  // (só na prática livre: no modo guiado a ferramenta é a da etapa)
  useEffect(() => {
    if (!abaAtiva || modoOficinaCorte !== 'pratica') return

    const aoTeclar = (evento: KeyboardEvent) => {
      const alvo = evento.target as HTMLElement | null
      if (alvo?.tagName === 'INPUT' || alvo?.tagName === 'TEXTAREA' || alvo?.isContentEditable) return
      if (evento.ctrlKey || evento.metaKey || evento.altKey) return

      const ferramenta = ferramentaPorAtalho(evento.key)
      if (!ferramenta) return

      evento.preventDefault()
      equiparFerramentaPorAtalho(ferramenta.id, evento.shiftKey)
    }

    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [abaAtiva, modoOficinaCorte, equiparFerramentaPorAtalho])

  // Ações de preparo da peça
  const acoesDePreparoDaPeca: FerramentaDaTrilha[] = useMemo(() => {
    if (pecaOficinaCorte === 'porta') {
      return [
        {
          id: 'manejar-vidro',
          rotulo: 'Manejar vidro',
          Icone: IconeVidro,
          aoAcionar: gerenciarVidroCorte,
          motivoIndisponivel: estadosVidros[vidroCorte] !== 'intacto'
            ? 'O vidro desta porta já foi gerenciado'
            : estadoPortaCorte.removida ? 'A porta já foi retirada' : undefined,
        },
        {
          id: 'testar-porta',
          rotulo: 'Testar abertura',
          Icone: IconeInteriorExtracao,
          aoAcionar: () => executarAcaoManualPorta('testarAberturaManual'),
          motivoIndisponivel: estadoPortaCorte.removida ? 'A porta já foi retirada' : undefined,
        },
        {
          id: 'sustentar-porta',
          rotulo: 'Sustentar porta',
          Icone: IconeSustentarPeca,
          aoAcionar: () => executarAcaoManualPorta('sustentarPorta'),
          ativa: estadoPortaCorte.sustentada,
          motivoIndisponivel: estadoPortaCorte.removida ? 'A porta já foi retirada' : undefined,
        },
        {
          id: 'retirar-porta',
          rotulo: 'Retirar porta',
          Icone: IconeRetirarPeca,
          aoAcionar: () => executarAcaoManualPorta('removerPorta'),
          motivoIndisponivel: portaProntaParaRemocao(estadoPortaCorte)
            ? undefined
            : 'Libere os vínculos e sustente a porta antes de retirar',
        },
      ]
    }
    if (pecaOficinaCorte === 'tampaTraseira') {
      return [
        {
          id: 'testar-tampa',
          rotulo: 'Testar abertura',
          Icone: IconeInteriorExtracao,
          aoAcionar: () => executarAcaoManualTampa('testarAberturaManual'),
          motivoIndisponivel: estadoTampaCorte.removida ? 'A tampa já foi retirada' : undefined,
        },
        {
          id: 'sustentar-tampa',
          rotulo: 'Sustentar tampa',
          Icone: IconeSustentarPeca,
          aoAcionar: () => executarAcaoManualTampa('sustentarTampa'),
          ativa: estadoTampaCorte.sustentada,
          motivoIndisponivel: estadoTampaCorte.removida ? 'A tampa já foi retirada' : undefined,
        },
        {
          id: 'retirar-tampa',
          rotulo: 'Retirar tampa',
          Icone: IconeRetirarPeca,
          aoAcionar: () => executarAcaoManualTampa('removerTampa'),
          motivoIndisponivel: tampaProntaParaRemocao(estadoTampaCorte)
            ? undefined
            : 'Libere trava, dobradiças e apoio a gás e sustente a tampa antes de retirar',
        },
      ]
    }
    return [
      {
        id: 'sustentar-aba',
        rotulo: 'Sustentar aba',
        Icone: IconeSustentarPeca,
        aoAcionar: () => executarAcaoManualTerceiraPorta('sustentarAba'),
        ativa: estadoTerceiraPortaCorte.sustentada,
        motivoIndisponivel: estadoTerceiraPortaCorte.removida ? 'A chapa já foi retirada' : undefined,
      },
      {
        id: 'remover-aba',
        rotulo: 'Rebater chapa',
        Icone: IconeRetirarPeca,
        aoAcionar: () => executarAcaoManualTerceiraPorta('removerAba'),
        motivoIndisponivel: abaProntaParaRemocao(estadoTerceiraPortaCorte)
          ? undefined
          : 'Conclua os três cortes e sustente a chapa antes de rebater',
      },
    ]
  }, [
    pecaOficinaCorte,
    gerenciarVidroCorte,
    estadosVidros,
    vidroCorte,
    estadoPortaCorte,
    executarAcaoManualPorta,
    estadoTampaCorte,
    executarAcaoManualTampa,
    estadoTerceiraPortaCorte,
    executarAcaoManualTerceiraPorta,
  ])

  const gruposDaCaixaDeFerramentasCorte = useMemo(() => [
    {
      id: 'ferramentas-intervencao',
      rotulo: 'Caixa de ferramentas',
      ferramentas: FERRAMENTAS_DE_INTERVENCAO.map((ferramenta) => ({
        id: ferramenta.id,
        rotulo: ferramenta.nome,
        atalho: ferramenta.atalho,
        Icone: ferramenta.Icone,
        aoAcionar: () => escolherFerramentaCorte(ferramenta.id),
        ativa: ferramentaCorte === ferramenta.id,
        motivoIndisponivel: motivoDaFerramentaDeCorte(ferramenta.id),
      })),
    },
    ...(ferramentaCorte === 'alargador'
      ? [{
          id: 'modo-alargador',
          rotulo: 'Modo do alargador',
          ferramentas: MODOS_DO_ALARGADOR.map((modo) => ({
            id: `alargador-${modo.id}`,
            rotulo: modo.nome,
            atalho: ATALHO_MODO_ALARGADOR,
            Icone: modo.Icone,
            aoAcionar: () => escolherModoAlargador(modo.id),
            ativa: modoAlargador === modo.id,
          })),
        }]
      : []),
    {
      id: 'preparo-peca',
      rotulo: 'Preparo e retirada da peça',
      ferramentas: acoesDePreparoDaPeca,
    },
    {
      id: 'equipamento-apoio',
      rotulo: 'Equipamento de apoio',
      ferramentas: EQUIPAMENTOS_DE_APOIO.map((equipamento) => ({
        id: equipamento.id,
        rotulo: equipamento.nome,
        Icone: equipamento.Icone,
        aoAcionar: () => {},
        motivoIndisponivel: MOTIVO_EQUIPAMENTO_DE_APOIO,
      })),
    },
  ], [
    ferramentaCorte,
    escolherFerramentaCorte,
    motivoDaFerramentaDeCorte,
    modoAlargador,
    escolherModoAlargador,
    acoesDePreparoDaPeca,
  ])

  const interacaoPorta = useMemo(() => ({
    aoSelecionarZona: (zona: IdZonaOperacaoPorta) => {
      setZonaOperacaoCorte(zona)
      const zonaInfo = zonaOperacaoPorId(perfil, idPortaCorte, zona)
      if (zonaInfo) {
        setAjusteFerramentaCorte({ ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zonaInfo.anguloGraus })
      }
      const intencao = resolverInteracaoPorta({
        ferramenta: ferramentaCorte,
        zona,
        modoAlargador,
        estado: estadosPortasCorte[idPortaCorte],
        portaDianteira: idPortaCorte.startsWith('portaDianteira'),
      })
      if (intencao.disponivel) {
        setOperacaoPortaCorte(intencao.operacao)
        setMensagemInteracaoCorte(`${intencao.descricao}: segure sobre o alvo para acionar.`)
      } else {
        setMensagemInteracaoCorte(intencao.descricao)
      }
    },
    aoMoverFerramenta: () => {},
    aoPressionarFerramenta: (zona: IdZonaOperacaoPorta) => {
      aoRegistrarHistoricoRef.current?.()
      setZonaOperacaoCorte(zona)
      const zonaInfo = zonaOperacaoPorId(perfil, idPortaCorte, zona)
      if (zonaInfo) {
        setAjusteFerramentaCorte({ ...AJUSTE_FERRAMENTA_INICIAL, anguloGraus: zonaInfo.anguloGraus })
      }
      const intencao = resolverInteracaoPorta({
        ferramenta: ferramentaCorte,
        zona,
        modoAlargador,
        estado: estadosPortasCorte[idPortaCorte],
        portaDianteira: idPortaCorte.startsWith('portaDianteira'),
      })
      if (intencao.disponivel) {
        setOperacaoPortaCorte(intencao.operacao)
      }
      setSegurandoFerramentaCorte(true)
    },
    aoSoltarFerramenta: () => {
      pausarAcionamentoFerramenta()
    },
    aoPrevisualizarZona: (zona: IdZonaOperacaoPorta) => {
      const intencao = resolverInteracaoPorta({
        ferramenta: ferramentaCorte,
        zona,
        modoAlargador,
        estado: estadosPortasCorte[idPortaCorte],
        portaDianteira: idPortaCorte.startsWith('portaDianteira'),
      })
      setMensagemInteracaoCorte(intencao.descricao)
    },
  }), [
    perfil,
    idPortaCorte,
    ferramentaCorte,
    modoAlargador,
    estadosPortasCorte,
    pausarAcionamentoFerramenta,
  ])

  const interacaoTampa = useMemo(() => ({
    aoSelecionarZona: (zona: IdZonaOperacaoTampa) => {
      setZonaOperacaoTampaCorte(zona)
      const intencao = resolverInteracaoTampa({ ferramenta: ferramentaCorte, zona, estado: estadoTampaCorte })
      if (intencao.disponivel) {
        setOperacaoTampaCorte(intencao.operacao)
        setMensagemInteracaoCorte(`${intencao.descricao}: segure para acionar a ferramenta.`)
      } else {
        setMensagemInteracaoCorte(intencao.descricao)
      }
    },
    aoMoverFerramenta: () => {},
    aoPressionarFerramenta: (zona: IdZonaOperacaoTampa) => {
      aoRegistrarHistoricoRef.current?.()
      setZonaOperacaoTampaCorte(zona)
      const intencao = resolverInteracaoTampa({ ferramenta: ferramentaCorte, zona, estado: estadoTampaCorte })
      if (intencao.disponivel) {
        setOperacaoTampaCorte(intencao.operacao)
      }
      setSegurandoFerramentaCorte(true)
    },
    aoSoltarFerramenta: () => {
      pausarAcionamentoFerramenta()
    },
    aoPrevisualizarZona: (zona: IdZonaOperacaoTampa) => {
      const intencao = resolverInteracaoTampa({ ferramenta: ferramentaCorte, zona, estado: estadoTampaCorte })
      setMensagemInteracaoCorte(intencao.descricao)
    },
  }), [ferramentaCorte, estadoTampaCorte, pausarAcionamentoFerramenta])

  const interacaoTerceiraPorta = useMemo(() => ({
    aoSelecionarZona: (zona: IdZonaOperacaoTerceiraPorta) => {
      setZonaOperacaoTerceiraPortaCorte(zona)
      const intencao = resolverInteracaoTerceiraPorta({ ferramenta: ferramentaCorte, zona, estado: estadoTerceiraPortaCorte })
      if (intencao.disponivel) {
        setOperacaoTerceiraPortaCorte(intencao.operacao)
        setMensagemInteracaoCorte(`${intencao.descricao}: segure para acionar a ferramenta.`)
      } else {
        setMensagemInteracaoCorte(intencao.descricao)
      }
    },
    aoMoverFerramenta: () => {},
    aoPressionarFerramenta: (zona: IdZonaOperacaoTerceiraPorta) => {
      aoRegistrarHistoricoRef.current?.()
      setZonaOperacaoTerceiraPortaCorte(zona)
      const intencao = resolverInteracaoTerceiraPorta({ ferramenta: ferramentaCorte, zona, estado: estadoTerceiraPortaCorte })
      if (intencao.disponivel) {
        setOperacaoTerceiraPortaCorte(intencao.operacao)
      }
      setSegurandoFerramentaCorte(true)
    },
    aoSoltarFerramenta: () => {
      pausarAcionamentoFerramenta()
    },
    aoPrevisualizarZona: (zona: IdZonaOperacaoTerceiraPorta) => {
      const intencao = resolverInteracaoTerceiraPorta({ ferramenta: ferramentaCorte, zona, estado: estadoTerceiraPortaCorte })
      setMensagemInteracaoCorte(intencao.descricao)
    },
  }), [ferramentaCorte, estadoTerceiraPortaCorte, pausarAcionamentoFerramenta])

  // Snapshots para histórico
  const obterInstantaneoHistorico = useCallback((): InstantaneoHistoricoOficina => ({
    portas: estadosPortasCorte,
    progresso: progressoEstruturalPortas,
    tampa: estadoTampaCorte,
    progressoTampa: progressoEstruturalTampa,
    terceirasPortas: estadosTerceirasPortasCorte,
    progressoTerceirasPortas: progressoEstruturalTerceirasPortas,
    extensor: estadoExtensorCorte,
    progressoExtensor: progressoEstruturalExtensor,
    vidros: estadosVidros,
    detritosNaVitima,
    detritosNasProtecoes,
  }), [
    estadosPortasCorte,
    progressoEstruturalPortas,
    estadoTampaCorte,
    progressoEstruturalTampa,
    estadosTerceirasPortasCorte,
    progressoEstruturalTerceirasPortas,
    estadoExtensorCorte,
    progressoEstruturalExtensor,
    estadosVidros,
    detritosNaVitima,
    detritosNasProtecoes,
  ])

  const restaurarInstantaneoHistorico = useCallback((i: InstantaneoHistoricoOficina) => {
    setEstadosPortasCorte(i.portas)
    setProgressoEstruturalPortas(i.progresso)
    setEstadoTampaCorte(i.tampa)
    setProgressoEstruturalTampa(i.progressoTampa)
    setEstadosTerceirasPortasCorte(i.terceirasPortas)
    setProgressoEstruturalTerceirasPortas(i.progressoTerceirasPortas)
    setEstadoExtensorCorte(i.extensor)
    setProgressoEstruturalExtensor(i.progressoExtensor)
    setEstadosVidros(i.vidros)
    setDetritosNaVitima(i.detritosNaVitima)
    setDetritosNasProtecoes(i.detritosNasProtecoes)
    setSegurandoFerramentaCorte(false)
    setProgressoFerramentaCorte(0)
    setResultadoOperacaoCorte(null)
    setResultadoIntervencaoCorte(null)
    setMensagemInteracaoCorte('Estado da oficina restaurado pelo histórico.')
  }, [setEstadosVidros, setDetritosNaVitima, setDetritosNasProtecoes])

  return {
    // Modos e Seleções
    modoOficinaCorte,
    setModoOficinaCorte,
    pecaOficinaCorte,
    setPecaOficinaCorte,
    ferramentaCorte,
    setFerramentaCorte,
    modoAlargador,
    setModoAlargador,
    mensagemInteracaoCorte,
    setMensagemInteracaoCorte,

    // Porta
    vidroCorte,
    setVidroCorte,
    idPortaCorte,
    estadoPortaCorte,
    estadosPortasCorte,
    setEstadosPortasCorte,
    atualizarEstadoPortaCorte,
    zonaOperacaoCorte,
    setZonaOperacaoCorte,
    operacaoPortaCorte,
    setOperacaoPortaCorte,
    resultadoOperacaoCorte,
    resultadoIntervencaoCorte,
    progressoFerramentaCorte,
    setProgressoFerramentaCorte,
    progressoEstruturalPortas,
    progressoEstruturalPortaAtual,
    atualizarProgressoEstruturalPorta,
    ajusteFerramentaCorte,
    setAjusteFerramentaCorte,
    posicaoFerramentaCorte,
    contatoFerramentaCorte,
    progressoSaidaPortaCorte,

    // Extensor
    estadoExtensorCorte,
    estadoExtensorVisual,
    setEstadoExtensorCorte,
    progressoEstruturalExtensor,
    condicaoEstabilizacaoExtensor,
    fluxoExtensorPronto,

    // Tampa Traseira
    estadoTampaCorte,
    setEstadoTampaCorte,
    progressoEstruturalTampa,
    operacaoTampaCorte,
    setOperacaoTampaCorte,
    resultadoOperacaoTampaCorte,
    zonaOperacaoTampaCorte,
    setZonaOperacaoTampaCorte,
    ajusteFerramentaTampaCorte,
    setAjusteFerramentaTampaCorte,
    posicaoFerramentaTampaCorte,
    contatoFerramentaTampaCorte,

    // Terceira Porta
    ladoTerceiraPortaCorte,
    setLadoTerceiraPortaCorte,
    idTerceiraPortaCorte,
    estadoTerceiraPortaCorte,
    estadosTerceirasPortasCorte,
    setEstadoTerceiraPortaCorte,
    progressoEstruturalTerceirasPortas,
    operacaoTerceiraPortaCorte,
    setOperacaoTerceiraPortaCorte,
    resultadoOperacaoTerceiraPortaCorte,
    zonaOperacaoTerceiraPortaCorte,
    setZonaOperacaoTerceiraPortaCorte,
    ajusteFerramentaTerceiraPortaCorte,
    setAjusteFerramentaTerceiraPortaCorte,
    posicaoFerramentaTerceiraPortaCorte,
    contatoFerramentaTerceiraPortaCorte,

    // Interação
    segurandoFerramentaCorte,
    setSegurandoFerramentaCorte,
    progressoEstruturalAtual,

    // Player
    playerCorte,
    ajusteFerramentaPlayerCorte,

    // Ações
    escolherFerramentaCorte,
    escolherModoAlargador,
    equiparFerramentaPorAtalho,
    motivoDaFerramentaDeCorte,
    executarAcaoPorta,
    executarAcaoManualPorta,
    gerenciarVidroCorte,
    executarAcaoTampa,
    executarAcaoManualTampa,
    executarAcaoTerceiraPorta,
    executarAcaoManualTerceiraPorta,
    pausarAcionamentoFerramenta,
    resetarEstadosOficinaCorte,
    acoesDePreparoDaPeca,
    gruposDaCaixaDeFerramentasCorte,
    interacaoPorta,
    interacaoTampa,
    interacaoTerceiraPorta,

    // Histórico
    obterInstantaneoHistorico,
    restaurarInstantaneoHistorico,
  }
}
